import {
  useAddAttachmentToProductInfoRequestMutation,
  useDeleteProductInfoRequestMutation,
  useGetProductInfoRequestDetailsQuery,
  useUpdateProductInfoRequestStatusMutation,
} from '@api/productRequest.api';
import { ProductRequestAttachment, ProductRequestStatusType } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  BackButton,
  BlockContainer,
  DocumentList,
  LoadingError,
  ProductRequestDelete,
  ProductRequestDetailsSkeleton,
  ProductRequestInfoMessages,
  ProductRequestProductBox,
  ProductRequestStatus,
  SmartLink,
  Title,
} from '@components';
import { useAppSelector, useAuth, useToast } from '@hooks';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Input,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { colors } from '@themes/colors';
import { t } from '@translations';
import { formatBytes } from '@utils/formatBytes.utils';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB in bytes

type ProductInfoRequestDetailsBlockProps = {
  title?: string;
};

type RequestAddAttachmentForm = {
  file: File;
};

export const ProductInfoRequestDetailsBlock: React.FC<ProductInfoRequestDetailsBlockProps> = ({
  title,
}: ProductInfoRequestDetailsBlockProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { toast } = useToast();
  const { reset, control } = useForm<RequestAddAttachmentForm>();
  const { isBeremaUser } = useAuth();
  const { pathname } = location;
  const currentPage = useAppSelector(getSpecialPage('productInfoRequestDetailsPage')) as string;
  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');
  const [updateStatusTrigger] = useUpdateProductInfoRequestStatusMutation();
  const [deleteTrigger] = useDeleteProductInfoRequestMutation();
  const { currentData, isLoading, isError } = useGetProductInfoRequestDetailsQuery(id || '', {
    skip: !id,
  });
  const [uploadAttachment] = useAddAttachmentToProductInfoRequestMutation();
  const isBreakpointMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const requestDate = dayjs(currentData?.timestamp, { utc: true }).format('DD.MM.YYYY HH:mm');
  const productInfoRequestListPage = useAppSelector(getSpecialPage('productInfoRequestListPage'));
  const [newAttachments, setNewAttachments] = useState<ProductRequestAttachment[]>([]);
  const isLoadingError = (isError || !id) && currentPage?.includes(pathname);
  const parseAttachments = (attachments: ProductRequestAttachment[]) => {
    return attachments.map((attachment) => {
      return {
        displayName: attachment.fileName,
        fileSize: formatBytes(attachment.length),
        url: `/api/product/info-request/${currentData?.id}/attachment/${attachment.id}/download`,
      };
    });
  };

  const combinedAttachments = [...(currentData?.attachments || []), ...newAttachments];
  const parsedAttachments = parseAttachments(combinedAttachments);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];

      if (file.size > MAX_FILE_SIZE) {
        toast({
          severity: 'error',
          message: t(TextCodes.ProductRequest.Base.UploadDocumentsLimitError, {
            fileName: file.name,
          }),
        });
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        await uploadAttachment({ formData, requestId: id ?? undefined }).then((res) => {
          if ('data' in res) {
            toast({
              severity: 'success',
              message: t(TextCodes.Base.UploadFileSuccess),
            });
            const attachment = res.data;
            setNewAttachments((prevAttachments) => [...prevAttachments, attachment]);
            reset();
          }
        });
      } catch (error) {
        toast({
          severity: 'error',
          message: 'error',
        });
      }

      target.value = '';
    }
  };

  const handleDropdownChange = (value: ProductRequestStatusType) => {
    if (currentData?.id) {
      updateStatusTrigger({ entityId: currentData?.id, status: value });
    }
  };

  const handleDelete = () => {
    if (currentData?.id) {
      deleteTrigger(currentData?.id).then(() => {
        if (productInfoRequestListPage) {
          navigate(productInfoRequestListPage);
        }
      });
    }
  };

  if (isLoading) return <ProductRequestDetailsSkeleton />;

  return (
    <BlockContainer>
      <BackButton href={productInfoRequestListPage} />
      {/* Optional Title */}
      {title && (
        <Title variant='h2' sx={{ mb: 4 }}>
          {title}
        </Title>
      )}
      {isLoadingError ? (
        <LoadingError />
      ) : (
        <>
          <Box display='flex' justifyContent='space-between'>
            {/* Dealer Name */}
            <Title variant='h3' noDot>
              {currentData?.dealer?.name || ''}
            </Title>
            <Stack direction='row' spacing={2} alignItems='center'>
              {/* Product Request Info Status */}
              <ProductRequestStatus
                handleDropdownChange={handleDropdownChange}
                currentStatus={currentData?.status}
                canChangeStatus={isBeremaUser}
              />
              {isBeremaUser && <ProductRequestDelete handleDelete={handleDelete} />}
            </Stack>
          </Box>

          <Divider sx={{ mb: 2, mt: 1 }} />

          {/* Request Details Title */}
          <Title variant='h3' noDot>
            {t(TextCodes.ProductRequest.Base.RequestDetails)}
          </Title>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              {/* Request Details */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                mb={2}
                mt={4}
                spacing={{
                  xs: 2,
                  md: 4,
                }}
              >
                {/* Request Date */}
                <div>
                  <Typography color={colors.text.secondary} fontWeight='bold !important'>
                    {t(TextCodes.ProductRequest.Base.RequestDate)}
                  </Typography>
                  <Typography variant='body2'>{requestDate}</Typography>
                </div>

                {/* Dealer Name */}
                <div>
                  <Typography color={colors.text.secondary} fontWeight='bold !important'>
                    {t(TextCodes.ProductRequest.Base.DealerName)}
                  </Typography>
                  <Typography variant='body2'>{currentData?.dealer?.name}</Typography>
                </div>
                <div>
                  {/* Created By */}
                  <Typography color={colors.text.secondary} fontWeight='bold !important'>
                    {t(TextCodes.ProductRequest.Base.CreatedBy)}
                  </Typography>
                  <SmartLink href={'mailto:' + currentData?.contact?.email}>
                    {currentData?.contact?.email}
                  </SmartLink>
                </div>
              </Stack>

              {/* Product Order Element */}
              <ProductRequestProductBox product={currentData?.product} />
              {/* Product Request Info Messages */}
              <ProductRequestInfoMessages
                author={currentData?.contact}
                messages={currentData?.messages}
                id={currentData?.id}
                question={currentData?.question}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Attachments */}
              <Box
                height='100%'
                mt={{
                  xs: 2,
                  md: 4,
                }}
              >
                <Typography color={colors.text.secondary} fontWeight='bold !important'>
                  {t(TextCodes.ProductRequest.Base.Attachments)}
                </Typography>

                <Box
                  mt={{
                    xs: 1,
                    md: 5,
                  }}
                  component='form'
                >
                  <DocumentList
                    documents={parsedAttachments}
                    isWorking={isLoading}
                    nogrid={isBreakpointMobile ? 'true' : undefined}
                  />

                  <Controller
                    control={control}
                    name='file'
                    render={({ field }) => {
                      return (
                        <FormControl sx={{ mt: 2 }}>
                          <Box width='100%'>
                            <label htmlFor='file' style={{ width: 'inherit', display: 'grid' }}>
                              <Button
                                component='span'
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                  backgroundColor: theme.palette.primary.main,
                                  ':hover': {
                                    backgroundColor: theme.palette.primary.light,
                                  },
                                }}
                                size='small'
                                variant='primary'
                              >
                                {t(TextCodes.ProductRequest.Base.UploadDocuments)}
                              </Button>
                            </label>
                            <Input
                              onChange={handleFileChange}
                              type='file'
                              id='file'
                              inputProps={{ accept: '.pdf,.jpg,.jpeg,.png' }}
                              sx={{ display: 'none' }}
                            />
                          </Box>
                        </FormControl>
                      );
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </BlockContainer>
  );
};
